export type QuantizeStrategy = 'nearest' | 'floor' | 'ceil';
type TimeUs = number;
export const MICROSECONDS_PER_SECOND = 1_000_000;

const DEFAULT_FPS = 30;

export const DEFAULT_FRAME_TOLERANCE_US: TimeUs = 33_333;

export function normalizeFps(value?: number): number {
  if (!Number.isFinite(value) || (value as number) <= 0) {
    return DEFAULT_FPS;
  }
  return value as number;
}

export function frameDurationFromFps(fps?: number): TimeUs {
  const normalized = normalizeFps(fps);
  const duration = MICROSECONDS_PER_SECOND / normalized;
  return Math.max(Math.round(duration), 1);
}

export function frameIndexFromTimestamp(
  baseTimestampUs: TimeUs,
  timestampUs: TimeUs,
  fps?: number,
  strategy: QuantizeStrategy = 'nearest'
): number {
  const frameDurationUs = frameDurationFromFps(fps);
  if (frameDurationUs <= 0) {
    return 0;
  }

  const delta = timestampUs - baseTimestampUs;
  const rawIndex = delta / frameDurationUs;

  if (!Number.isFinite(rawIndex)) {
    return 0;
  }

  switch (strategy) {
    case 'floor':
      return Math.floor(rawIndex);
    case 'ceil':
      return Math.ceil(rawIndex);
    default:
      return Math.round(rawIndex);
  }
}

export function quantizeTimestampToFrame(
  timestampUs: TimeUs,
  baseTimestampUs: TimeUs,
  fps?: number,
  strategy: QuantizeStrategy = 'nearest'
): TimeUs {
  const frameDurationUs = frameDurationFromFps(fps);
  const index = frameIndexFromTimestamp(baseTimestampUs, timestampUs, fps, strategy);
  return baseTimestampUs + index * frameDurationUs;
}

export function isTimestampWithinFrame(
  targetTimeUs: TimeUs,
  frameTimestampUs: TimeUs,
  frameDurationUs: TimeUs,
  toleranceUs: TimeUs = DEFAULT_FRAME_TOLERANCE_US
): boolean {
  if (!Number.isFinite(frameTimestampUs)) {
    return false;
  }

  const delta = Math.abs(targetTimeUs - frameTimestampUs);
  if (delta <= toleranceUs) {
    return true;
  }

  if (frameDurationUs > 0 && frameTimestampUs <= targetTimeUs) {
    return targetTimeUs < frameTimestampUs + frameDurationUs;
  }

  return false;
}
